import { Type, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ModalDialogOptions, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { HtmlNode, WINDOW } from '@frontend/vanilla/core';
import { BehaviorSubject, Observable, filter, fromEvent, skip, switchMap, takeUntil } from 'rxjs';

export abstract class SwitcherToggleService {
    private dialogRef?: ModalRef;
    private visible$ = new BehaviorSubject(false);
    readonly #window = inject(WINDOW);
    private readonly switcherEnabled: boolean;

    constructor(
        private dialogService: ModalDialogService,
        private htmlNode: HtmlNode,
        isSwitcherEnabled: boolean,
    ) {
        this.switcherEnabled = isSwitcherEnabled;

        const show$ = this.visible$.pipe(filter((v) => v));
        const hide$ = this.visible$.pipe(filter((v) => !v));
        show$
            .pipe(
                switchMap(() => fromEvent(this.#window, 'click').pipe(skip(1), takeUntil(hide$))),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.close());
    }

    get expanded$(): Observable<boolean> {
        return this.visible$.asObservable();
    }

    get expanded(): boolean {
        return this.visible$.getValue();
    }

    protected open<TComponent>(component: Type<TComponent>, options: ModalDialogOptions<Partial<TComponent> | void>): void {
        if (!this.switcherEnabled) {
            return;
        }
        this.toggleHtmlClasses(true);
        this.dialogRef = this.dialogService.openDialog(component, options);
        this.visible$.next(true);
    }

    close(): void {
        if (!this.expanded) {
            return;
        }
        this.toggleHtmlClasses(false);
        this.dialogRef?.close();
        this.dialogRef = undefined;
        this.visible$.next(false);
    }

    closeSwitcher(): void {
        this.close();
    }

    getHtmlToggleClass(): string {
        return 'event-switcher-visible';
    }

    private toggleHtmlClasses(visible: boolean): void {
        this.htmlNode.setCssClass(this.getHtmlToggleClass(), visible);
    }
}
